import { useEffect, useRef, useState } from "preact/hooks";

/**
 * In-page agent that drives the freshly-built MCP's tool surface. Calls
 * /api/agent (Anthropic proxy) with the tools attached, parses streaming
 * SSE responses, runs any tool_use blocks against the supplied `callTool`
 * adapter (which talks to the in-browser ToolRuntime), and feeds the
 * tool_result back into the next turn. The user types prompts, the agent
 * replies token-by-token — no pre-scripted text.
 *
 * Streaming protocol: Anthropic Messages API streaming. We listen for
 * content_block_delta events (text + input_json_delta), and stop_reason
 * end_turn / tool_use on message_stop.
 */
export interface AgentToolSpec {
  name: string;
  description: string;
  input_schema: unknown;
}

export interface AgentChatProps {
  tools: AgentToolSpec[];
  systemPrompt: string;
  callTool: (name: string, args: Record<string, unknown>) => Promise<unknown>;
  /** Optional initial assistant greeting. */
  greeting?: string;
  /** Optional user-turn auto-fired ~600ms after mount. Booth demo bait. */
  autoFirstTurn?: string;
}

interface TurnMsg {
  role: "user" | "assistant" | "tool";
  content: string;
  toolUseId?: string;
  /** Decorations the UI renders inline. */
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  toolResult?: unknown;
}

const SUGGESTED_PROMPTS = [
  "what tools do you have?",
  "show me a couple of items",
  "look up a specific record by id",
  "what would happen if I tried to delete everything?"
];

export function AgentChat(props: AgentChatProps) {
  const [history, setHistory] = useState<TurnMsg[]>(
    props.greeting ? [{ role: "assistant", content: props.greeting }] : []
  );
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [history.length]);

  // Auto-fire one provocative user turn on mount, so a walk-by attendee
  // sees tokens streaming + a tool call within ~2 seconds of arrival. The
  // ref guard ensures we don't refire if React strict-mode double-renders.
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (!props.autoFirstTurn) return;
    firedRef.current = true;
    const t = setTimeout(() => { void send(props.autoFirstTurn ?? ""); }, 600);
    return () => clearTimeout(t);
  }, []);

  const send = async (userText: string): Promise<void> => {
    if (busy) return;
    const text = userText.trim();
    if (text.length === 0) return;
    setError(undefined);
    setInput("");
    setBusy(true);
    const localHist: TurnMsg[] = [...history, { role: "user", content: text }];
    setHistory(localHist);

    try {
      // Up to 4 tool-use rounds per user prompt — enough for any of the
      // demo specs without runaway calls.
      let roundsLeft = 4;
      while (roundsLeft-- > 0) {
        const turnRes = await runOneTurn(localHist, props.tools, props.systemPrompt);
        // Append the assistant turn token by token as it arrives.
        let assistantSoFar = "";
        const pendingToolUses: Array<{ id: string; name: string; jsonAcc: string }> = [];
        const idx = localHist.length;
        localHist.push({ role: "assistant", content: "" });
        setHistory([...localHist]);
        for await (const ev of turnRes.events) {
          if (ev.type === "text") {
            assistantSoFar += ev.text;
            localHist[idx] = { ...localHist[idx], content: assistantSoFar };
            setHistory([...localHist]);
          } else if (ev.type === "tool_start") {
            pendingToolUses.push({ id: ev.id, name: ev.name, jsonAcc: "" });
          } else if (ev.type === "tool_args_delta") {
            const tu = pendingToolUses.find((t) => t.id === ev.id);
            if (tu) tu.jsonAcc += ev.partial;
          } else if (ev.type === "error") {
            throw new Error(ev.message);
          }
        }
        if (pendingToolUses.length === 0) break; // pure-text turn, done
        // Execute each tool against the in-browser runtime; feed results back.
        for (const tu of pendingToolUses) {
          let args: Record<string, unknown>;
          try { args = (tu.jsonAcc ? JSON.parse(tu.jsonAcc) : {}) as Record<string, unknown>; }
          catch { args = {}; }
          localHist[idx] = {
            ...localHist[idx],
            toolName: tu.name,
            toolArgs: args
          };
          setHistory([...localHist]);
          let result: unknown;
          try { result = await props.callTool(tu.name, args); }
          catch (e) { result = { error: (e as Error).message }; }
          localHist[idx] = {
            ...localHist[idx],
            toolResult: result
          };
          setHistory([...localHist]);
          localHist.push({
            role: "tool",
            content: typeof result === "string" ? result : JSON.stringify(result).slice(0, 4096),
            toolUseId: tu.id
          });
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ marginTop: 20 }}>
      <h2 className="bracket-cap">TALK TO YOUR MCP</h2>
      <p className="dim" style={{ fontSize: 13, marginTop: 8 }}>
        The agent connects to the {props.tools.length} tools you just built. Tool calls run
        against an in-browser runtime — no third-party services.
      </p>
      <div
        ref={scrollerRef}
        style={{
          background: "#0c0c0c",
          border: "1px solid #222",
          borderRadius: 6,
          padding: 12,
          maxHeight: 380,
          overflowY: "auto",
          fontFamily: "system-ui, sans-serif",
          marginTop: 8
        }}
      >
        {history.length === 0 && (
          <div className="dim" style={{ fontSize: 13 }}>
            Try one of these, or ask anything:
            <ul style={{ marginTop: 6 }}>
              {SUGGESTED_PROMPTS.map((p) => (
                <li key={p}>
                  <button
                    style={{
                      all: "unset",
                      cursor: "pointer",
                      color: "#e9c46a",
                      textDecoration: "underline dotted"
                    }}
                    onClick={(): Promise<void> => send(p)}
                  >
                    {p}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {history.map((m, i) => (
          <Bubble key={i} msg={m} />
        ))}
        {busy && <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>thinking…</div>}
      </div>
      {error && (
        <p style={{ color: "#e57a7a", fontSize: 13, marginTop: 6 }}>
          agent error — {error}
        </p>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <input
          value={input}
          onInput={(e): void => setInput((e.target as HTMLInputElement).value)}
          onKeyDown={(e): void => {
            if ((e as KeyboardEvent).key === "Enter") void send(input);
          }}
          placeholder="ask the MCP anything…"
          disabled={busy}
          style={{
            flex: 1,
            padding: "8px 10px",
            background: "#0c0c0c",
            color: "#eee",
            border: "1px solid #333",
            borderRadius: 4,
            fontFamily: "monospace"
          }}
        />
        <button onClick={(): Promise<void> => send(input)} disabled={busy || input.trim().length === 0}>
          send
        </button>
      </div>
    </section>
  );
}

function Bubble({ msg }: { msg: TurnMsg }): preact.JSX.Element {
  if (msg.role === "tool") return <></>; // tool_result blocks render inside the assistant bubble above
  const isUser = msg.role === "user";
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
      <div
        style={{
          maxWidth: "85%",
          padding: "8px 10px",
          borderRadius: 8,
          background: isUser ? "#1a2a32" : "#10180f",
          border: isUser ? "1px solid #2c4452" : "1px solid #20371e",
          color: "#e8e8e8",
          fontSize: 14,
          whiteSpace: "pre-wrap"
        }}
      >
        {msg.content || <span className="dim">…</span>}
      </div>
      {msg.toolName && (
        <div style={{ marginTop: 6, padding: "6px 10px", background: "#161108", border: "1px solid #2a2510", borderRadius: 6, fontSize: 12, fontFamily: "monospace", color: "#f2e1b3", maxWidth: "85%" }}>
          <div>→ <strong>{msg.toolName}</strong>(<span style={{ color: "#999" }}>{JSON.stringify(msg.toolArgs ?? {}).slice(0, 200)}</span>)</div>
          {msg.toolResult !== undefined && (
            <div style={{ marginTop: 4, color: "#7ad48c" }}>
              ← {(typeof msg.toolResult === "string" ? msg.toolResult : JSON.stringify(msg.toolResult)).slice(0, 240)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SSE parsing ──────────────────────────────────────────────────────────────
type AgentEvent =
  | { type: "text"; text: string }
  | { type: "tool_start"; id: string; name: string }
  | { type: "tool_args_delta"; id: string; partial: string }
  | { type: "error"; message: string }
  | { type: "done" };

async function runOneTurn(
  history: TurnMsg[],
  tools: AgentToolSpec[],
  systemPrompt: string
): Promise<{ events: AsyncIterable<AgentEvent> }> {
  const res = await fetch("/api/agent", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      system: systemPrompt,
      messages: history.map((m) =>
        m.role === "tool"
          ? { role: "tool", content: m.content, toolUseId: m.toolUseId }
          : { role: m.role, content: m.content }
      ),
      tools: tools.length > 0 ? tools : undefined
    })
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`agent /api/agent ${res.status}: ${text.slice(0, 240)}`);
  }
  return { events: parseAnthropicSse(res) };
}

async function* parseAnthropicSse(res: Response): AsyncIterable<AgentEvent> {
  const reader = res.body?.getReader();
  if (!reader) {
    yield { type: "error", message: "no response body" };
    return;
  }
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  // index id (assistant-side block index) → tool use id mapping
  const toolIds: Record<number, string> = {};
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const dataStr = line.slice(5).trim();
      if (dataStr === "" || dataStr === "[DONE]") continue;
      let evt: { type: string; index?: number; delta?: Record<string, unknown>; content_block?: Record<string, unknown>; message?: Record<string, unknown> };
      try { evt = JSON.parse(dataStr); } catch { continue; }
      if (evt.type === "content_block_start" && evt.content_block?.type === "tool_use") {
        const id = String(evt.content_block.id);
        const name = String(evt.content_block.name);
        toolIds[evt.index ?? 0] = id;
        yield { type: "tool_start", id, name };
      } else if (evt.type === "content_block_delta") {
        const d = evt.delta;
        if (d?.type === "text_delta" && typeof d.text === "string") {
          yield { type: "text", text: d.text };
        } else if (d?.type === "input_json_delta" && typeof d.partial_json === "string") {
          const id = toolIds[evt.index ?? 0];
          if (id) yield { type: "tool_args_delta", id, partial: d.partial_json };
        }
      } else if (evt.type === "message_stop") {
        yield { type: "done" };
        return;
      }
    }
  }
  yield { type: "done" };
}
